<template>
  <div class="information-card__wrapper" :class="collapsibleClass">
    <wb-card class="information-card" v-if="showCard">
      <div class="information-card__top-row" v-if="showTopRow">
        <div class="information-card__top-row--time">
          <div class="information-card__top-row--time-top">
            <div
              class="information-card__top-row--date wb-type-heading-xs"
              :class="disabledClass"
            >
              {{ date }}
            </div>
            <div class="information-card__top-row--tag-container">
              <div
                class="information-card__top-row--tag wb-type-hint-strong"
                :class="tagStyles"
                v-if="computedTagText"
              >
                {{ computedTagText }}
              </div>
              <wb-tooltip-host
                position="top"
                class="information-card__top-row--tag-tooltip"
                v-if="infoTooltipMessage"
              >
                <wb-icon :name="infoToolipIcon"></wb-icon>
                <wb-tooltip>
                  {{ infoTooltipMessage }}
                </wb-tooltip>
              </wb-tooltip-host>
            </div>
          </div>
          <div
            class="information-card__top-row--time-bottom wb-type-copy-strong"
            :class="disabledClass"
            v-if="time"
          >
            <template>
              <wb-icon name="time" />
              {{ time }}
            </template>
          </div>
        </div>
        <div class="information-card__top-row--aside">
          <div
            class="information-card__top-row--booking-id wb-type-copy-strong"
            :class="[disabledClass, disabledClassBookingId]"
          >
            <template v-if="id">#{{ id }}</template>
          </div>
          <button
            type="button"
            aria-label="Toggle Collapsible"
            class="wb-round-button wb-round-button--solid-level-0 wb-round-button--small information-card__collapse-button"
            :class="collapseButtonRTL"
            v-if="computedCollapsible"
            @click="toggleCollapsedState()"
          >
            <wb-icon
              class="wb-round-button__icon"
              aria-hidden="true"
              :name="collapsibleButtonIconName"
              flip-rtl
            ></wb-icon>
          </button>
        </div>
      </div>
      <div class="information-card__collapsible" :style="collapsibleStyle">
        <div
          class="information-card__middle-row"
          v-if="showMiddleRow"
          ref="infoCardMiddleRow"
        >
          <div class="left-block" :class="leftBlockRTL">
            <div class="dealer-information">
              <dealer-information
                :dealerInformation="dealerInformation"
                :disabled="disabled"
                v-if="!isMobile"
              />
            </div>
            <div class="additional-info mobile-hidden" v-if="additionalInfo">
              <div class="additional-info-title wb-type-copy-strong">
                {{ $t('summary.additionalInfo.label') }}
              </div>
              <div class="additional-info-message wb-type-copy-secondary">
                {{ additionalInfo }}
              </div>
            </div>

            <wb-card class="instructions mobile-hidden" v-if="showInstructions">
              <div class="instructions--left" :class="instructionsLeftRTL">
                <wb-icon
                  class="instructions--left-icon"
                  :name="instructionsIcon"
                />
              </div>
              <div class="instructions--right">
                <div
                  class="instructions-title"
                  :class="instructionsTitleDesktopClass"
                >
                  {{ instructionsTitle }}
                </div>
                <div class="instructions-message wb-type-copy-secondary">
                  {{ instructionsMessage }}
                </div>
                <div class="instructions-link">
                  <a
                    @click="trackInstructionsLinkClick"
                    :href="instructionsLink"
                    class="instructions-link-anchor wb-link wb-link--standalone wb-type-copy-secondary"
                    >{{ instructionsLinkLabel }}</a
                  >
                </div>
              </div>
            </wb-card>
          </div>
          <div class="right-block">
            <dealer-information
              class="dealer-information-mobile"
              :dealerInformation="dealerInformation"
              :disabled="disabled"
              v-if="isMobile"
            />
            <vehicle-information
              class="vehicle-information"
              :disabled="disabled"
              :vehicleInformation="vehicleInformation"
            />
          </div>

          <div class="additional-info desktop-hidden" v-if="additionalInfo">
            <div class="additional-info-title wb-type-copy-strong">
              {{ $t('summary.additionalInfo.label') }}
            </div>
            <div class="additional-info-message wb-type-copy-secondary">
              {{ additionalInfo }}
            </div>
          </div>

          <wb-card class="instructions desktop-hidden" v-if="showInstructions">
            <div class="instructions--left">
              <wb-icon
                :name="instructionsIcon"
                class="instructions--left-icon"
              />
            </div>
            <div class="instructions--right">
              <div
                class="instructions-title"
                :class="instructionsTitleMobileClass"
              >
                {{ instructionsTitle }}
              </div>
              <div class="instructions-message wb-type-copy-secondary">
                {{ instructionsMessage }}
              </div>
              <div class="instructions-link">
                <a
                  @click="trackInstructionsLinkClick"
                  :href="instructionsLink"
                  class="instructions-link-anchor wb-link wb-link--standalone wb-type-copy-strong"
                  >{{ instructionsLinkLabel }}</a
                >
              </div>
            </div>
          </wb-card>
        </div>
        <div
          class="information-card__bottom-row"
          ref="infoCardBottomRow"
          v-if="desktopButtons"
        >
          <div
            class="information-card__bottom-row--buttons"
            v-for="(button, key) in bottomRowButtons"
            :key="`button-${key}`"
          >
            <a @click="buttonAction(button)">
              <button
                class="wb-button wb-button--large information-card__bottom-row--buttons--item"
                :class="getButtonVariantClass(button.variant)"
              >
                <wb-icon :name="button.icon" v-if="button.icon" />
                {{ button.label }}
              </button>
            </a>
          </div>
        </div>
      </div>
    </wb-card>
    <wb-card>
      <div
        class="information-card__bottom-row-mobile--card"
        :style="collapsibleMobileRowStyle"
      >
        <div
          class="information-card__bottom-row-mobile"
          ref="infoCardMobileBottomRow"
          v-if="mobileButtons"
        >
          <div
            class="information-card__bottom-row--buttons"
            v-for="(button, key) in bottomRowButtons"
            :key="`button-${key}`"
          >
            <a @click="buttonAction(button)">
              <button
                class="wb-button wb-button--large information-card__bottom-row--buttons--item"
                :class="getButtonVariantClass(button.variant)"
              >
                <wb-icon :name="button.icon" v-if="button.icon" />
                {{ button.label }}
              </button>
            </a>
          </div>
        </div>
      </div>
    </wb-card>

    <transition name="fade" mode="out-in">
      <wb-modal
        v-if="showCancellationModal"
        :mode="modalMode"
        close-on-backdrop-click
        close-on-escape-key
        scroll-lock
        ref="modalPreferences"
      >
        <wb-modal-header></wb-modal-header>
        <wb-modal-content :headline="modalCancellationTitle">
          <template v-if="!showAEMForm">
            <h5 class="wb-type-heading-xs modal-subtitle">
              {{ $t('cancellation.copy') }}
            </h5>
          </template>
          <template v-else>
            <template v-if="cancellationSubmitted">
              <h5 class="wb-type-copy modal-response-title">
                {{ modalCancellationResponse }}
              </h5>
              <button
                v-if="isLastStepDesktopCancellation"
                type="button"
                class="wb-button wb-button--primary wb-button--large"
                @click="leaveCancellation(true)"
              >
                {{ $t('message.cta') }}
              </button>
            </template>
            <template v-else>
              <widget-loader :resources="aemFormsResources" />
              <aemf-form-container
                formname="aemf-otb-cancellation"
                :appid="appId"
                @aemf-form-submit-success="aemFormSuccess"
                :formurl="aemFormUrl"
              />
              <template v-if="!aemFormLoaded">
                <div>&nbsp;</div>
              </template>
            </template>
          </template>
        </wb-modal-content>
        <wb-modal-footer :class="getFooterClasses">
          <button
            v-if="isFirstStepCancellation"
            type="button"
            class="wb-button wb-button--large"
            :class="buttonType"
            @click="leaveCancellation(false)"
          >
            {{ $t('cancellation.leave.cta') }}
          </button>
          <button
            v-if="isFirstStepCancellation"
            type="button"
            class="wb-button wb-button--primary wb-button--large"
            @click="proceedCancellation"
          >
            {{ $t('cancellation.cta') }}
          </button>
          <button
            v-if="isLastStepMobileCancellation"
            type="button"
            class="wb-button wb-button--primary wb-button--large"
            @click="leaveCancellation(true)"
          >
            {{ $t('message.cta') }}
          </button>
        </wb-modal-footer>
      </wb-modal>
      <!-- RESCHEDULE MODAL -->
      <wb-modal
        v-if="showRescheduleModal"
        :mode="modalMode"
        close-on-backdrop-click
        close-on-escape-key
        scroll-lock
        @wbclose="leaveReschedule(false)"
      >
        <div
          class="reschedule__spinner"
          :class="{ hidden: !rescheduleSpinner }"
        >
          <wb-spinner theme="light"></wb-spinner>
        </div>
        <wb-modal-header></wb-modal-header>
        <wb-modal-content :headline="modalRescheduleTitle">
          <template v-if="!showRescheduleSuccess">
            <p class="wb-type-copy reschedule__instructions">
              {{ $t('reschedule.testDrive.subtitle') }}
            </p>
            <datetime-picker
              :activeTextCarouselSlide="rescheduleCalendarPage"
              :dealerCalendar="rescheduleCalendar"
              :labelPlaceholder="$t('schedule.datepicker.start.placeholder')"
              :locale="localeCode"
              :isMobile="isMobile"
              :selectedDates="selectedRescheduleDay"
              :selectedSlot="selectedRescheduleTimeSlot"
              :timeOptions="rescheduleTimeslots"
              :timeOptionsLabel="$t('schedule.timeSlot.label')"
              @changeDate="changeRescheduleDay"
              @changeSheet="changeReschedulePage"
              @selectedSlot="changeRescheduleSlot"
            ></datetime-picker>
          </template>
          <template v-else>
            <h5
              class="wb-type-copy reschedule__success-message"
              v-html="
                $t('reschedule.testDrive.success.message', {
                  timePlaceLabel: testDriveBookingInfo,
                })
              "
            ></h5>
          </template>
        </wb-modal-content>
        <wb-modal-footer :class="hiddenPromptFooterClass">
          <button
            v-if="!showRescheduleSuccess"
            type="button"
            class="wb-button wb-button--secondary wb-button--large"
            @click="leaveReschedule(false)"
          >
            {{ $t('cancellation.leave.cta') }}
          </button>
          <button
            v-if="!showRescheduleSuccess"
            type="button"
            class="wb-button wb-button--primary wb-button--large"
            :disabled="isScheduledDate"
            @click="submitReschedule"
          >
            {{ $t('reschedule.testDrive.confirm') }}
          </button>
          <button
            v-if="showRescheduleSuccess"
            type="button"
            class="wb-button wb-button--primary wb-button--large reschedule__success-btn"
            @click="leaveRescheduleAfterSuccess"
          >
            {{ $t('message.cta') }}
          </button>
        </wb-modal-footer>
      </wb-modal>
    </transition>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, onMounted, onUpdated } from 'vue';
import { TranslateResult } from 'vue-i18n';

import { DealerInformation, VehicleInformation, WidgetLoader } from '../index';

import { useCancellation } from '../../composables/cancellation.composable';
import { useDirection } from '../../composables/direction.composable';
import { useReschedule } from '../../composables/reschedule.composable';
import { useUtils } from '../../composables/utils.composable';
import { useStore } from '../../composables/store.composable';
import { useMediaQuery } from '../../composables/media-query.composable';
import useBooking from '../../composables/booking.composable';

import { SchedulePreference } from '../../graphql/schema';

import {
  BottomRowButtonAction,
  BottomRowButtonTarget,
  DisabledLineThrough,
  DisabledBookingId,
  CollapsedCard,
} from '../../models/entities/information-card.model';
import { VehicleTrackingInfo } from '../../models/entities/tracking.model';
import { Variant } from '../../models/utilities/variant.enum';
import { Icon } from '../../models/utilities/icon.enum';
import GenericReceivedObject from '../../models/utilities/generic-received-object.model';

import { i18n } from '../../services/translation.service';
import DateService from '../../services/date.service';

import { SkeletonDirective } from '../../directives/skeleton.directive';

import { TRACKING_EVENT_LABEL } from '../../models/constants';

export default defineComponent({
  name: 'otb-information-card',
  components: {
    DatetimePicker: () => import('../datetime-picker/index.vue'),
    VehicleInformation,
    DealerInformation,
    WidgetLoader,
  },
  props: {
    additionalInfo: {
      type: String,
    },
    bottomRowButtons: {
      type: Array,
    },
    collapsible: {
      type: Boolean,
    },
    date: {
      type: String,
    },
    dealerId: {
      type: String,
    },
    dealerInformation: {
      type: Array as () => GenericReceivedObject[],
    },
    disabled: {
      type: Boolean,
    },
    id: {
      type: String,
      default: '',
    },
    infoToolipIcon: {
      type: String,
      default: Icon.INFO_CIRCLE,
    },
    infoTooltipMessage: {
      type: String,
    },
    instructionsIcon: {
      type: String,
      default: Icon.INFO,
    },
    instructionsTitle: {
      type: String,
    },
    instructionsLink: {
      type: String,
    },
    instructionsLinkLabel: {
      type: String,
    },
    instructionsMessage: {
      type: String,
    },
    instructionsLinkTrackingLabel: {
      type: String as () => TRACKING_EVENT_LABEL,
    },
    modelSeriesId: {
      type: String,
    },
    schedule: {
      type: Object as () => SchedulePreference,
    },
    tagText: {
      type: String,
    },
    tagVariant: {
      type: String,
    },
    trackingVehicle: {
      type: Object as () => VehicleTrackingInfo,
    },
    time: {
      type: String,
    },
    vehicleId: {
      type: String,
    },
    vehicleInformation: {
      type: Object,
    },
  },
  directives: {
    skeleton: SkeletonDirective,
  },
  setup(props, { emit }) {
    const collapsible = computed((): boolean => {
      return props.collapsible || cancellationSuccess.value;
    });

    const buttonType = computed(() => {
      return isMobile.value ? 'wb-button--tertiary' : 'wb-button--secondary';
    });

    const { trackBookingClickEvent } = useBooking();

    const {
      activateCancellationModal,
      aemFormLoaded,
      cancellationSubmitted,
      cancellationSuccess,
      showAEMForm,
      showCancellationModal,
      isFirstStepCancellation,
      isLastStepCancellation,
      isLastStepDesktopCancellation,
      isLastStepMobileCancellation,
      modalPreferences,
      aemFormsResources,
      aemFormUrl,
      appId,
      modalCancellationTitle,
      modalCancellationResponse,
      aemFormSuccess,
      hiddenPromptFooterClass,
      getFooterClasses,
      leaveCancellation,
      proceedCancellation,
    } = useCancellation(props.id, props.trackingVehicle);

    const { isRTL } = useDirection();

    const {
      activateRescheduleModal,
      changeRescheduleDay,
      changeReschedulePage,
      changeRescheduleSlot,
      isScheduledDate,
      leaveReschedule,
      leaveRescheduleAfterSuccess,
      modalRescheduleTitle,
      rescheduleCalendar,
      rescheduleCalendarPage,
      rescheduleSpinner,
      rescheduleTimeslots,
      selectedRescheduleDay,
      selectedRescheduleTimeSlot,
      showRescheduleModal,
      showRescheduleSpinner,
      showRescheduleSuccess,
      submitReschedule,
      testDriveBookingInfo,
    } = useReschedule(props.id, emit, props.trackingVehicle);

    const { getter, state } = useStore();

    const {
      // refs
      collapsed,
      collapsibleHeight,
      collapsibleMobileRowHeight,
      // template refs
      infoCardMiddleRow,
      infoCardBottomRow,
      infoCardMobileBottomRow,
      // computed
      collapsibleStyle,
      collapsibleMobileRowStyle,
      collapsibleButtonIconName,
      icon,
      // methods
      toggleCollapsedState,
      setCollapsibleHeight,
    } = useUtils(collapsible);

    const { isMobile } = useMediaQuery();

    onMounted(() => {
      window.addEventListener('resize', setCollapsibleHeight);
    });

    onUpdated(() => {
      setCollapsibleHeight();
    });

    const modalMode = computed((): string => {
      return isMobile.value ? 'edit' : 'prompt';
    });

    const computedTagText = computed((): TranslateResult | string => {
      return !cancellationSuccess.value
        ? props.tagText || ''
        : i18n.t('otbs.informationCard.tag.cancelled');
    });

    // ----------------------------#region-----------------------------
    // ---------------------TEMPLATE RELATED COMPUTED METHODS----------
    // ----------------------------------------------------------------
    const instructionsLeftRTL = computed((): string => {
      return isRTL.value ? 'instructions--left-rtl' : '';
    });

    const leftBlockRTL = computed((): string => {
      return isRTL.value ? 'left-block--rtl' : '';
    });

    const collapseButtonRTL = computed((): string => {
      return isRTL.value ? 'information-card__collapse-button--rtl' : '';
    });

    const tagStyles = computed((): string => {
      const tagVariant = cancellationSuccess.value
        ? Variant.SECONDARY
        : props.tagVariant;
      const tagVariantClass = tagVariant
        ? `information-card__top-row--tag-${tagVariant}`
        : '';

      const tagStyleClass = isRTL.value
        ? `${tagVariantClass} information-card__top-row--tag-rtl`
        : tagVariantClass;
      return tagStyleClass;
    });

    const disabledClass = computed((): DisabledLineThrough => {
      return {
        'disabled-class--line-through':
          props.disabled || cancellationSuccess.value,
        'disabled-class--line-through-rtl': isRTL.value,
      };
    });

    const disabledClassBookingId = computed((): DisabledBookingId => {
      return {
        'disabled-class--booking-id':
          props.disabled || cancellationSuccess.value,
      };
    });

    const collapsibleClass = computed((): CollapsedCard => {
      return {
        'information-card--collapsed':
          collapsed.value && (props.collapsible || cancellationSuccess.value),
      };
    });

    const showButtons = computed((): boolean => {
      return !!(
        props.bottomRowButtons &&
        props.bottomRowButtons?.length > 0 &&
        !cancellationSuccess.value
      );
    });

    const desktopButtons = computed((): boolean => {
      return !!(showButtons.value && !isMobile.value);
    });

    const mobileButtons = computed((): boolean => {
      return !!(showButtons.value && isMobile.value);
    });

    const showMiddleRow = computed((): boolean => {
      return !!(
        props.additionalInfo ||
        props.dealerInformation ||
        props.vehicleInformation
      );
    });

    const showTopRow = computed((): boolean => {
      return !!(
        props.date ||
        props.time ||
        props.tagText ||
        props.id ||
        props.collapsible ||
        cancellationSuccess.value
      );
    });

    const showCard = computed((): boolean => {
      return showTopRow.value || showMiddleRow.value;
    });

    const showInstructions = computed((): boolean => {
      return !!(
        props.instructionsTitle ||
        props.instructionsMessage ||
        props.instructionsLink
      );
    });

    const instructionsTitleMobileClass = computed(() => {
      return {
        'wb-type-copy-strong': !props.instructionsLink,
        'wb-type-copy-secondary': props.instructionsLink,
      };
    });

    const instructionsTitleDesktopClass = computed(() => {
      return {
        'wb-type-button': !props.instructionsLink,
        'wb-type-copy-tertiary': props.instructionsLink,
      };
    });

    const trackInstructionsLinkClick = () => {
      if (props.instructionsLinkTrackingLabel) {
        trackBookingClickEvent(
          props.instructionsLinkTrackingLabel,
          props.trackingVehicle,
          props.dealerId
        );
      }
    };

    const activeDealerCalendar = computed(() => {
      return DateService.getDatesAvailability(state.testDriveBookingsCalendar);
    });

    const localeCode = computed((): string => getter.localeCode);

    const getButtonVariantClass = (variant?: string): string => {
      let formatedVariant = variant;
      if (isMobile.value && variant === Variant.SECONDARY) {
        formatedVariant = Variant.TERTIARY;
      }
      return formatedVariant ? `wb-button--${formatedVariant}` : '';
    };

    const buttonAction = (button) => {
      if (button.action === BottomRowButtonAction.MODAL) {
        switch (button.target) {
          case BottomRowButtonTarget.CANCELLATION:
            activateCancellationModal(props.dealerId);
            break;
          case BottomRowButtonTarget.RESCHEDULE:
            activateRescheduleModal(
              props.dealerId,
              props.dealerInformation ? props.dealerInformation[0].key : '',
              props.vehicleId,
              props.modelSeriesId,
              props.schedule
            );
            break;
        }
      } else {
        if (button.trackingLabel) {
          trackBookingClickEvent(
            button.trackingLabel,
            props.trackingVehicle,
            props.dealerId
          );
        }
        window.open(button.link, button.target);
      }
    };

    return {
      // cancellation composition
      activeDealerCalendar,
      aemFormLoaded,
      cancellationSubmitted,
      showAEMForm,
      showCancellationModal,
      isFirstStepCancellation,
      isLastStepCancellation,
      isLastStepDesktopCancellation,
      isLastStepMobileCancellation,
      modalPreferences,
      aemFormsResources,
      aemFormUrl,
      appId,
      modalCancellationTitle,
      modalCancellationResponse,
      aemFormSuccess,
      buttonAction,
      hiddenPromptFooterClass,
      getFooterClasses,
      leaveCancellation,
      proceedCancellation,
      // reschedule composition
      changeRescheduleDay,
      changeReschedulePage,
      changeRescheduleSlot,
      isScheduledDate,
      leaveReschedule,
      leaveRescheduleAfterSuccess,
      modalRescheduleTitle,
      rescheduleCalendar,
      rescheduleCalendarPage,
      rescheduleSpinner,
      rescheduleTimeslots,
      selectedRescheduleDay,
      selectedRescheduleTimeSlot,
      showRescheduleModal,
      showRescheduleSpinner,
      showRescheduleSuccess,
      submitReschedule,
      testDriveBookingInfo,
      // utils
      collapsed,
      collapsibleHeight,
      collapsibleMobileRowHeight,
      infoCardMiddleRow,
      infoCardBottomRow,
      infoCardMobileBottomRow,
      collapsibleStyle,
      collapsibleMobileRowStyle,
      collapsibleButtonIconName,
      icon,
      isMobile,
      matchMedia,
      toggleCollapsedState,
      setCollapsibleHeight,
      // information-card
      buttonType,
      isRTL,
      modalMode,
      instructionsLeftRTL,
      leftBlockRTL,
      collapseButtonRTL,
      instructionsTitleDesktopClass,
      instructionsTitleMobileClass,
      trackInstructionsLinkClick,
      tagStyles,
      disabledClass,
      disabledClassBookingId,
      computedCollapsible: collapsible,
      collapsibleClass,
      computedTagText,
      desktopButtons,
      mobileButtons,
      localeCode,
      showMiddleRow,
      showTopRow,
      showCard,
      showInstructions,
      getButtonVariantClass,
    };
  },
});
</script>
<style scoped lang="scss">
@import './information-card.scss';
@import '~workbench-core/dist/scss/globals';

.modal-subtitle {
  display: block;
  @include wb-spacing('margin', 'xs 0 m 0');
  color: var(--wb-grey-20);
}

.modal-response-title {
  @include wb-spacing('margin-bottom', 'l');
}

// This is a temporary hack untill the WB version that seamless uses is
// bumped up to get the new color schema
.wb-button--primary {
  background-color: var(--wb-blue-45) !important;
  color: var(--wb-white) !important;
  &:hover {
    background-color: var(--wb-blue-25) !important;
  }
  wb-icon {
    color: var(--wb-white) !important;
  }
}

.wb-button--tertiary {
  color: var(--wb-blue-45);
  margin-bottom: 0;
}

wb-modal-content {
  color: var(--wb-grey-20);

  h3,
  h5 {
    flex-basis: 100%;
  }

  button {
    margin-left: auto;
  }
}

wb-modal[mode='edit'] wb-modal-footer {
  @include breakpoint-to(mq3) {
    @include wb-spacing('padding', 'xxs xs m xs');

    &.last-step-footer {
      @include wb-spacing('padding', 's xs m xs');
    }
  }
}

.hidden-prompt-footer {
  padding: 0 !important;
}

:deep {
  @include breakpoint-to(mq2) {
    wb-modal[mode='edit'] wb-modal-footer :not(:last-child) {
      display: inherit;
    }
    .wb-modal__outer {
      height: 100%;
      background-color: red;
    }
  }
}
</style>
